import { GetJobQuery, JobStatus } from "generated-graphql/graphql";
import { useEffect, useRef } from "react";

import { useCheckForFile } from "./useCheckForFile";
import { useLongPollingForJob } from "./useLongPollingForJob";

interface UseLongPollingForJobWithFileOutputOptions {
  timeoutInSeconds?: number;
  onUrlsReady: (urls: string[]) => void;
  onError?: (error: Error) => void;
}

interface UseLongPollingForJobWithFileOutputResult {
  polling: boolean;
  job: GetJobQuery["job"] | undefined;
  pollingTimedOut: boolean;
  startPolling: (jobId: string) => void;
}

/**
 * This hook is used to poll for a job and fetch the file URLs when the job is successful.
 * It is a wrapper around the useLongPollingForJob hook and the useCheckForFile hook.
 */
export function useLongPollingForJobWithFileOutput({
  timeoutInSeconds,
  onUrlsReady,
  onError,
}: UseLongPollingForJobWithFileOutputOptions): UseLongPollingForJobWithFileOutputResult {
  const { polling, result, startPolling } = useLongPollingForJob({
    timeoutInSeconds,
  });
  const { fetchFile } = useCheckForFile();
  const hasCalledUrlsReady = useRef(false);

  // Reset state when polling starts
  useEffect(() => {
    if (polling) {
      hasCalledUrlsReady.current = false;
    }
  }, [polling]);

  // When job completes successfully, fetch file URLs and call onUrlsReady
  useEffect(() => {
    const fetchFiles = async () => {
      if (!result.job || result.pollingTimedOut) return;
      if (result.job.status !== JobStatus.Succeeded) return;
      const output = result.job.output;
      if (!output) return;
      try {
        let keys: string[] = [];
        if (Array.isArray(output.downloadKeys) && output.downloadKeys.length > 0) {
          keys = output.downloadKeys;
        } else if (output.s3Key) {
          keys = [output.s3Key];
        }
        if (keys.length === 0) {
          onUrlsReady([]);
          hasCalledUrlsReady.current = true;
          return;
        }
        const urls: string[] = [];
        for (const key of keys) {
          const url = await fetchFile(key);
          if (url) {
            urls.push(url);
          } else {
            throw new Error(`Failed to fetch file for key: ${key}`);
          }
        }
        onUrlsReady(urls);
        hasCalledUrlsReady.current = true;
      } catch (err) {
        if (onError) {
          onError(err instanceof Error ? err : new Error("Unknown error fetching files"));
        }
      }
    };
    if (
      result.job &&
      !result.pollingTimedOut &&
      result.job.status === JobStatus.Succeeded &&
      !hasCalledUrlsReady.current
    ) {
      fetchFiles();
    }
  }, [result, fetchFile, onUrlsReady, onError]);

  return {
    polling,
    job: result.job,
    pollingTimedOut: result.pollingTimedOut,
    startPolling,
  };
}
